// Pillows site footer rendered in-tree by PageSkeleton (#33285 P3/P5).
//
// The footer navigation is resolved server-side from the "footer" NavMenu into
// typed columns (chrome._build_footer_nav) and rendered here as a real React
// tree -- the .footer-nav desktop + .footer-nav-mobile mobile <nav> blocks.
// Every link carries an explicit target, so the shared links.ts widget (which
// only rewrites untargeted links) leaves them alone: no pre-hydration DOM
// mutation, no hydration mismatch. Each mobile section owns its collapse state
// (MobileFooterBlock below). Only footer_copy_html remains injected -- it is
// the FooterSettings RichText StreamField (the CMS-RichText exception).
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type FooterNavItem = {
    block_type: string;
    label: string;
    url: string;
    target: string;
    html: string;
};

type FooterNavColumn = {
    title: string;
    items: readonly FooterNavItem[];
};

type IProps = Immutable<{
    phone_display: string;
    phone_href: string;
    copyright_year: string;
    footer_copy_html: string;
    footer_nav: readonly FooterNavColumn[];
    staticBase: string;
}>;

// One collapsible mobile footer section. Clicking the header toggles
// the block's active modifier (component state), which reveals the dropdown.
const MobileFooterBlock = (
    props: Immutable<{ column: FooterNavColumn }>,
): React.JSX.Element => {
    const { column } = props;
    const [active, setActive] = React.useState(false);
    return (
        <div
            className={concatClassNames([
                styles.footerNavMobileBlock,
                active ? styles.footerNavMobileBlockActive : null,
            ])}
        >
            <h3
                className={concatClassNames([
                    styles.footerNavMobileHeader,
                    styles.siteFooterHeader,
                ])}
                role="button"
                tabIndex={0}
                aria-expanded={active}
                onClick={() => {
                    setActive((value) => !value);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActive((value) => !value);
                    }
                }}
            >
                {column.title}
            </h3>
            <div className={styles.siteFooterDropdown}>
                {column.items.map((item, ii) =>
                    item.block_type === "html" ? (
                        // Raw-HTML block (the OneTrust cookie-settings button).
                        // The OneTrust SDK mutates this button in place before
                        // hydration, so suppress the mismatch warning on this
                        // externally-managed opaque subtree.
                        <EditorContent key={ii}>
                            {(marker) => (
                                <div
                                    suppressHydrationWarning
                                    dangerouslySetInnerHTML={{
                                        __html: item.html,
                                    }}
                                    {...marker}
                                />
                            )}
                        </EditorContent>
                    ) : (
                        <div
                            className={concatClassNames([
                                styles.footerNavItem,
                                styles.footerNavItemMobile,
                            ])}
                            key={ii}
                        >
                            <a
                                className={styles.footerNavItemMobileLink}
                                href={item.url}
                                target={item.target}
                            >
                                {item.label}
                            </a>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};

export const SiteFooter = (props: IProps): React.JSX.Element => {
    const {
        phone_display,
        phone_href,
        copyright_year,
        footer_copy_html,
        footer_nav,
        staticBase,
    } = props;
    const a360Alt = t`An Accessible360 badge sharing that we've partnered with an industry-leading ADA vendor who maintains our site is in good standing with accessibility.`;
    return (
        <footer
            className={concatClassNames([
                "site-footer",
                styles.siteFooter,
                "l-full-width",
            ])}
        >
            <div className="l-capped-width">
                <div
                    className={concatClassNames([
                        styles.siteFooterHeader,
                        styles.siteFooterHeaderContainer,
                    ])}
                >
                    <div className={styles.siteFooterHeaderPhoneNumber}>
                        {phone_display && (
                            <a href={phone_href}>
                                <div
                                    className={concatClassNames([
                                        styles.siteFooterHeaderButton,
                                        styles.siteFooterHeaderPhoneNumberIcon,
                                        "button",
                                    ])}
                                >
                                    {phone_display}
                                </div>
                            </a>
                        )}
                    </div>
                </div>

                <nav
                    className={concatClassNames([
                        styles.footerNav,
                        "l-capped-width",
                    ])}
                >
                    {footer_nav.map((column, ci) => (
                        <div className={styles.footerNavCol} key={ci}>
                            <ul className={styles.footerNavList}>
                                <h3
                                    className={styles.siteFooterHeader}
                                    role="heading"
                                    aria-level={2}
                                >
                                    {column.title}
                                </h3>
                                {column.items.map((item, ii) =>
                                    item.block_type === "html" ? (
                                        // Raw-HTML block (the OneTrust cookie-
                                        // settings button). The OneTrust SDK
                                        // mutates this button in place before
                                        // hydration, so suppress the mismatch
                                        // warning on this externally-managed
                                        // opaque subtree.
                                        <EditorContent key={ii}>
                                            {(marker) => (
                                                <div
                                                    suppressHydrationWarning
                                                    dangerouslySetInnerHTML={{
                                                        __html: item.html,
                                                    }}
                                                    {...marker}
                                                />
                                            )}
                                        </EditorContent>
                                    ) : (
                                        <li
                                            className={styles.footerNavItem}
                                            key={ii}
                                        >
                                            <a
                                                href={item.url}
                                                target={item.target}
                                            >
                                                {item.label}
                                            </a>
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    ))}
                </nav>

                <nav
                    className={concatClassNames([
                        styles.footerNavMobile,
                        "u-margin-bottom--xl",
                    ])}
                >
                    {footer_nav.map((column, ci) => (
                        <MobileFooterBlock key={ci} column={column} />
                    ))}
                </nav>

                <div
                    className={concatClassNames([
                        styles.footerBadges,
                        "l-capped-width",
                    ])}
                >
                    <div className={styles.footerBadgesItemA360}>
                        <a
                            href="https://allyant.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                className="responsive-img"
                                src={`${staticBase}img/icons/badge-A360.png`}
                                alt={a360Alt}
                                loading="lazy"
                                decoding="async"
                            />
                        </a>
                    </div>
                </div>

                <div className={styles.siteFooterLegal}>
                    &copy; {copyright_year} Tempur-Pedic North America
                    LLC.&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                    <a href="/legal/privacy-policy/">{t`Privacy Policy`}</a>
                    &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                    <a href="/legal/terms-of-use/">{t`Terms & Conditions`}</a>
                    <div
                        className="footer-legal__aux-copy"
                        dangerouslySetInnerHTML={{ __html: footer_copy_html }}
                    />
                </div>
            </div>
        </footer>
    );
};
